import { Helmet } from "react-helmet-async";
import { useContext } from "react";
import { LangContext } from "../App";
import { IntlProvider } from "react-intl";
import { motion, AnimatePresence } from "framer-motion";
import { textVariants } from "../components/textVariants";

const Competences = () => {
  const language = useContext(LangContext);
  const techSkills_pl = [
    "Projektowanie i tworzenie aplikacji webowych SPA z wykorzystaniem React.js.",
    "Budowa responsywnych interfejsów użytkownika z użyciem Material UI, Bootstrap i CSS.",
    "Tworzenie backendów oraz interfejsów REST API w PHP (Slim Framework).",
    "Projektowanie struktur baz danych oraz optymalizacja zapytań SQL (MySQL, MSSQL).",
    "Integracja aplikacji z zewnętrznymi usługami i API.",
    "Automatyzacja procesów biznesowych i przepływu danych.",
    "Projektowanie architektury aplikacji oraz organizacja logiki biznesowej.",
    "Wdrażanie i utrzymanie aplikacji na serwerach Linux i VPS.",
    "Konfiguracja środowisk Docker i procesów wdrożeniowych.",
    "Administracja domenami, DNS, pocztą elektroniczną i certyfikatami SSL.",
    "Zabezpieczanie aplikacji, kontrola dostępu i tworzenie kopii zapasowych.",
    "Monitoring, utrzymanie i rozwój systemów produkcyjnych.",
  ];
  const techSkills_en = [
    "Development of modern Single Page Applications (SPA) using React.js.",
    "Design and implementation of responsive user interfaces with Bootstrap, Material UI, and CSS3.",
    "Backend development and REST API implementation in PHP.",
    "Design, optimization, and maintenance of MySQL and Microsoft SQL Server databases.",
    "Integration with third-party systems and business process automation.",
    "Web application architecture design and data flow planning.",
    "Deployment and maintenance of applications on VPS servers and Linux environments.",
    "Docker configuration and deployment process automation.",
    "Domain, DNS, email service, and SSL certificate configuration.",
    "Application security, data protection, and backup strategies.",
    "Application performance optimization and technical SEO.",
    "Monitoring, maintenance, and continuous improvement of production systems.",
  ];
  const managementSkills_pl = [
    "Kierowanie zespołami IT oraz koordynacja pracy programistów i administratorów.",
    "Planowanie i realizacja projektów informatycznych – od analizy potrzeb biznesowych po wdrożenie i rozwój systemów.",
    "Współpraca z zarządem oraz klientami w zakresie definiowania wymagań i planowania rozwoju produktów.",
    "Optymalizacja procesów biznesowych poprzez projektowanie i wdrażanie dedykowanych rozwiązań informatycznych.",
    "Zarządzanie budżetem IT oraz kontrola kosztów utrzymania infrastruktury i systemów.",
    "Nadzór nad bezpieczeństwem, ciągłością działania oraz rozwojem środowiska IT.",
    "Zarządzanie pełnym cyklem życia aplikacji: analiza, projektowanie, wdrożenie, utrzymanie i rozwój.",
    "Tworzenie oraz rozwój własnych produktów internetowych i aplikacji wykorzystywanych komercyjnie.",
  ];
  const managementSkills_en = [
    "Managing the IT department and coordinating the work of software developers and system administrators.",
    "Planning and delivering IT projects from business requirements analysis through implementation and ongoing system development.",
    "Collaborating with executive management and clients to define business requirements and product development strategies.",
    "Optimizing business processes by designing and implementing tailored IT solutions.",
    "Managing IT budgets and optimizing infrastructure and system maintenance costs.",
    "Ensuring IT security, business continuity, and the continuous development of the IT environment.",
    "Managing the full application lifecycle, including analysis, design, implementation, maintenance, and continuous improvement.",
    "Creating and developing commercial web applications and proprietary digital products.",
  ];
  const styles = {
    wrapper: {
      minHeight: "100vh",
      padding: "80px 24px 60px 24px",
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      width: "100%",
    },
    headerZone: {
      textAlign: "center",
      marginBottom: "48px",
    },
    sectionSubtitle: {
      fontSize: "14px",
      fontWeight: "700",
      color: "#4f46e5",
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      marginBottom: "8px",
    },
    mainTitle: {
      fontSize: "36px",
      fontWeight: "800",
      color: "#0f172a",
      letterSpacing: "-0.03em",
      margin: 0,
    },
    splitLayout: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(450px, 100%), 1fr))",
      gap: "32px",
      alignItems: "start",
    },
    competenceCard: {
      background: "#ffffff",
      borderRadius: "24px",
      padding: "40px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 10px 30px -15px rgba(0,0,0,0.05)",
      boxSizing: "border-box",
    },
    card: {
      background: "#ffffff",
      borderRadius: "4px",
      padding: "36px",
      boxShadow: "0 10px 30px -10px rgba(0,0,0,0.04)",
      border: "1px solid #e2e8f0",
      height: "100%",
      boxSizing: "border-box",
    },
    cardHeaderGradient: {
      background: "linear-gradient(135deg, #1E3A5F 0%, #274C77 100%)",
      padding: "10px",
      borderRadius: "22px",
      textAlign: "left",
      marginBottom: "20px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    cardTitleGradient: {
      color: "#fff",
      fontSize: "20px",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "20px",
      paddingLeft: "10px",
    },
    iconWrapper: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "48px",
      height: "48px",
      borderRadius: "12px",
      backgroundColor: "#eef2ff",
      color: "#4f46e5",
      flexShrink: 0,
    },
    cardTitle: {
      fontSize: "18px",
      fontWeight: "700",
      color: "#0f172a",
      margin: 0,
    },
    list: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    listItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      fontSize: "15px",
      color: "#475569",
      lineHeight: "1.5",
    },
    checkIcon: {
      flexShrink: 0,
      marginTop: "3px",
      color: "#10b981",
    },
  };

  console.log("Rendering Competences component with language:", language);

  return (
    <>
      <Helmet>
        <title>
          Kompetencje IT – React, PHP, SQL i zarządzanie IT | Michał Jaskulski
        </title>

        <meta
          name="description"
          content="Poznaj kompetencje Michała Jaskulskiego: React, PHP, MySQL, MSSQL, REST API, administracja Linux, infrastruktura IT oraz zarządzanie projektami informatycznymi."
        />

        <link
          rel="canonical"
          href="https://michaljaskulski.pl/react-developer"
        />
      </Helmet>

      <AnimatePresence>
        <motion.div
          style={styles.container}
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={textVariants}
        >
          <div style={styles.wrapper}>
            <div style={styles.container}>
              <>
                <div style={styles.headerZone}>
                  {language === "pl" && (
                    <h1 style={styles.mainTitle}>
                      Kompetencje techniczne i doświadczenie w IT
                    </h1>
                  )}
                  {language === "en" && (
                    <h1 style={styles.mainTitle}>
                      Technical Skills and IT Management Experience
                    </h1>
                  )}

                  <p
                    style={{
                      maxWidth: "850px",
                      margin: "18px auto 0",
                      fontSize: "17px",
                      lineHeight: 1.7,
                      color: "#475569",
                      textAlign: "center",
                    }}
                  >
                    {language === "en" &&
                      "I combine IT management experience with hands-on software development. I design and develop web applications, database systems and integrations that improve everyday business operations. I understand both business requirements and the technical aspects of project delivery."}
                    {language === "pl" &&
                      "Łączę doświadczenie w zarządzaniu IT z praktycznym tworzeniem oprogramowania. Projektuję i rozwijam aplikacje webowe, systemy bazodanowe oraz integracje, które usprawniają codzienną pracę firm. Rozumiem zarówno potrzeby biznesowe, jak i techniczne aspekty realizacji projektów."}
                  </p>
                </div>
              </>
              <div style={styles.splitLayout}>
                <div style={styles.competenceCard}>
                  <div style={styles.cardHeader}>
                    {language === "pl" && (
                      <div style={styles.cardHeaderGradient}>
                        <h2 style={styles.cardTitleGradient}>
                          Umiejętności techniczne
                        </h2>
                      </div>
                    )}
                    {language === "en" && (
                      <div style={styles.cardHeaderGradient}>
                        <h2 style={styles.cardTitleGradient}>
                          Technical skills
                        </h2>
                      </div>
                    )}
                  </div>

                  {language === "pl" && (
                    <div style={styles.list}>
                      {techSkills_pl.map((skill, index) => (
                        <div key={index} style={styles.listItem}>
                          <svg
                            style={styles.checkIcon}
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span>{skill}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {language === "en" && (
                    <div style={styles.list}>
                      {techSkills_en.map((skill, index) => (
                        <div key={index} style={styles.listItem}>
                          <svg
                            style={styles.checkIcon}
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span>{skill}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={styles.competenceCard}>
                  <div style={styles.cardHeader}>
                    {language === "pl" && (
                      <div style={styles.cardHeaderGradient}>
                        <h2 style={styles.cardTitleGradient}>
                          Zarządzanie IT i projektami
                        </h2>
                      </div>
                    )}
                    {language === "en" && (
                      <div style={styles.cardHeaderGradient}>
                        <h2 style={styles.cardTitleGradient}>
                          IT Management & Project Delivery
                        </h2>
                      </div>
                    )}
                  </div>
                  {language === "pl" && (
                    <div style={styles.list}>
                      {managementSkills_pl.map((skill, index) => (
                        <div key={index} style={styles.listItem}>
                          <svg
                            style={styles.checkIcon}
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span>{skill}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {language === "en" && (
                    <div style={styles.list}>
                      {managementSkills_en.map((skill, index) => (
                        <div key={index} style={styles.listItem}>
                          <svg
                            style={styles.checkIcon}
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span>{skill}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default Competences;
